import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    BlockType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    Setting,
    CodeBlock,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils, {
    TIBBO_DEVICE_ID,
} from '@appblocks/platforms/PlatformUtils';
import LUISGroup from '@appblocks/codegeneration/SettingsConfigurations/LUISGroup';
import * as simulator from './simulator';

export default class DSOLED extends BaseFeature {
    constructor() {
        super();
        this.name = 'OLED Screen(SSD1305)';
        this.id = 'ds_oled';
        this.icon = 'CgScreen';
        this.requirements = [['DS1101N', 'DS1102N']];
        this.priority = 101;
        const settings: Array<Setting> = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'IMAGES_GROUP',
                name: 'Images',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'Enabled',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'BACKCOLOR',
                name: 'Background Color',
                settingType: SettingType.COLOR,
                group: 'GENERAL',
                default: '#000000',
                required: true,
            },
            {
                id: 'FORECOLOR',
                name: 'Foreground Color',
                settingType: SettingType.COLOR,
                group: 'GENERAL',
                default: '#ffffff',
                required: true,
            },
            {
                id: 'LOGFONT',
                name: 'Log Font Size',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: 'LCD_SMALL_FONT',
                options: [
                    {
                        name: 'Standard Font',
                        value: 'LCD_STD_FONT',
                    },
                    {
                        name: 'Small Font',
                        value: 'LCD_SMALL_FONT',
                    },
                ],
            },
            {
                id: 'SCREENS',
                name: 'Screens',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'display_screen',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'IMAGES',
                name: 'Images',
                settingType: SettingType.ARRAY,
                refId: 'lcd_image',
                required: true,
                group: 'IMAGES_GROUP',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'LCD_SCREEN_WIDGET_COLUMNS',
                name: 'LCD Screen Widget Columns',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 96,
            },
            {
                id: 'LCD_SCREEN_WIDGET_ROWS',
                name: 'LCD Screen Widget Rows',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 32,
            },
        ];

        this.settings = this.settings.concat(settings);
    }


    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                if (!this.getEnabled()) {
                    return SettingState.DISABLED;
                }
                return SettingState.ENABLED;
        }
    }

    getDependencies() {
        const dependencies = [];
        const SCREENS = this.getSettingValue('SCREENS');
        let widgetCount = 0;
        for (let i = 0; i < SCREENS.length; i++) {
            const widgets = SCREENS[i].getSettingValue('WIDGETS');
            widgetCount += widgets.length;
        }
        if (widgetCount > 0) {
            dependencies.push('display_lib');
        }

        for (let i = 0; i < this.project.features.length; i++) {
            if ((this.project.features[i].id === 'node_lcd'
                || this.project.features[i].id === 'node_lcd_graph')
                && !dependencies.includes('display_lib')
            ) {
                dependencies.push('display_lib');
                break;
            }
        }

        return dependencies;
    }

    getSettingsEnabled() {
        return false;
    }

    getFiles(): Array<string> {
        let { files } = this;

        files = files.concat([
            '11x19(HP).bin',
            'Tibbo-5x7(V).bin',
        ]);
        return files;
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];
        const widgets = this.getSettingValue('WIDGETS');

        defines.push({
            name: 'LCD_STD_FONT',
            value: '"11X19(HP).bin"',
        });
        defines.push({
            name: 'LCD_SMALL_FONT',
            value: '"Tibbo-5x7(V).bin"',
        });

        return defines;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: Array<CodeBlock> = [];
        if (this.project.runtime === 'simulator') {
            return simulator.getCodeBlocks(this);
        }
        if (!this.getEnabled()) {
            return blocks;
        }
        const screens = this.getSettingValue('SCREENS');

        const imageWidgets = [];
        const textWidgets = [];


        blocks.push({
            location: 'PROJECT_DEFINES',
            blockType: BlockType.CODE,
            id: '/dLCD_TYPE=SOLOMON_SSD1305',
            template: '/dLCD_TYPE=SOLOMON_SSD1305',
        });

        blocks.push({
            location: 'PROJECT_DEFINES',
            blockType: BlockType.CODE,
            id: '/dOBJECT_LCD=LCD_ENABLED',
            template: '/dOBJECT_LCD=LCD_ENABLED',
        });

        const { project } = this;
        const BACKCOLOR = PlatformUtils.hexToTibboColor(this.getSettingValue('BACKCOLOR'));
        const FORECOLOR = PlatformUtils.hexToTibboColor(this.getSettingValue('FORECOLOR'));
        const LOGFONT = this.getSettingValue('LOGFONT');
        const SCREEN_WIDTH = Number(this.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS'));
        const SCREEN_HEIGHT = Number(this.getSettingValue('LCD_SCREEN_WIDGET_ROWS'));

        const fontHeight = LOGFONT === 'LCD_STD_FONT' ? 19 : 8;
        const fontWidth = LOGFONT === 'LCD_STD_FONT' ? 11 : 5;
        const totalLines = Math.floor(SCREEN_HEIGHT / fontHeight) - 1;
        const stringSize = Math.floor(SCREEN_WIDTH / fontWidth);
        const maxStringSize = Math.floor(SCREEN_WIDTH / (fontWidth + 1));
        blocks.push({
            location: 'DEVICE_TBS_DIMS',
            blockType: BlockType.CODE,
            template: `
dim lcd_logs(${totalLines}) as string(${stringSize})
dim lcd_log_index as byte=0`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub lcd_log(text as string)`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
const LOG_FONT_FILE = ${LOGFONT}`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub lcd_log(text as string)
    dim i as byte
    if lcd_log_index < ${totalLines} - 1 then
        lcd_log_index = lcd_log_index + 1
    else 
        for i=0 to ${totalLines} - 2
            lcd_logs(i) = lcd_logs(i + 1)
        next i
    end if
    lcd_logs(lcd_log_index) = left(text,${maxStringSize})
    romfile.open(LOG_FONT_FILE)
    lcd.setfont(romfile.offset)
    lcd.lock()
    lcd.forecolor=${BACKCOLOR}
    lcd.fill(0,0,lcd.width,lcd.height)
    lcd.forecolor=${FORECOLOR}
    for i=0 to ${totalLines} - 1
        lcd.print(lcd_logs(i),0,i * ${fontHeight})
    next i
    lcd.unlock()
end sub`,
        });

        for (let j = 0; j < screens.length; j++) {
            const screen = screens[j];
            const widgets = screen.getSettingValue('WIDGETS');

            for (let i = 0; i < widgets.length; i++) {
                const widget = widgets[i];
                const type = widget.id;
                switch (type) {
                    case 'lcd_image_widget':
                        imageWidgets.push(widget);
                        break;
                    case 'lcd_text_widget':
                        textWidgets.push(widget);
                        break;
                    default:
                        break;
                }
            }
            for (let i = 0; i < widgets.length; i++) {
                const widget = widgets[i];
                blocks.push(...widget.getCodeBlocks());
            }
        }


        for (let i = 0; i < imageWidgets.length; i++) {
            const widget = imageWidgets[i];
            const widgetObject = widget.getWidgetObject();
            blocks.push({
                location: 'lcd_init_widgets',
                blockType: BlockType.CODE,
                template: `
lcd_img_widgets(${i}).filename="${widgetObject.img.split('\n')[0]}"
lcd_img_widgets(${i}).x_pos=${widgetObject.x}
lcd_img_widgets(${i}).y_pos=${widgetObject.y}
lcd_img_widgets(${i}).x_offset=${widgetObject.x_offset || 0}
lcd_img_widgets(${i}).y_offset=${widgetObject.y_offset || 0}
lcd_img_widgets(${i}).width=${widgetObject.w}
lcd_img_widgets(${i}).height=${widgetObject.h}
lcd_img_widgets(${i}).visible=${widget.getSettingValue('VISIBLE') === '1' ? 'true' : 'false'}
lcd_img_widgets(${i}).screen=${widgetObject.screen}
`,
            });
        }

        for (let i = 0; i < textWidgets.length; i++) {
            const widget = textWidgets[i];
            const widgetObject = widget.getWidgetObject();
            blocks.push({
                location: 'lcd_init_widgets',
                blockType: BlockType.CODE,
                template: `
lcd_text_widgets(${i}).text="${widgetObject.text}"
lcd_text_widgets(${i}).x_pos=${widgetObject.x}
lcd_text_widgets(${i}).y_pos=${widgetObject.y}
lcd_text_widgets(${i}).width=${widgetObject.w}
lcd_text_widgets(${i}).height=${widgetObject.h}
lcd_text_widgets(${i}).forecolor=${PlatformUtils.hexToTibboColor(widgetObject.color)}
lcd_text_widgets(${i}).backcolor=${PlatformUtils.hexToTibboColor(widgetObject.backgroundColor)}
lcd_text_widgets(${i}).font=${widgetObject.font}
lcd_text_widgets(${i}).text_alignment=${widgetObject.alignment}
lcd_text_widgets(${i}).visible=${widget.getSettingValue('VISIBLE') === '1' ? 'true' : 'false'}
lcd_text_widgets(${i}).screen=${widgetObject.screen}
`,
            });
        }

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    io.num=36
    io.enabled=YES

    io.num=35
    io.enabled=YES

    io.num=34
    io.enabled=YES

    io.num=33
    io.enabled=YES
    io.state=HIGH 'disabled

    io.num=32
    io.enabled=YES
    io.state=LOW 'permanently enabled

    lcd.width=96
    lcd.height=32
    lcd.iomapping="36,35,34,33,32,0" 'RST,DC,WR,RD,CS,data_bus

    lcd.enabled=YES`,
        });


        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define MENU_CURSOR_STYLE 1
#define LIST_BOX_CURSOR_STYLE 1
#define INPUT_BOX_CURSOR_STYLE 1
#define LCD_LOCK lcd.lock
#define LCD_UNLOCK lcd.unlock
#define VER_SPACING 9
#define HOR_SPACING 2 

#define BMP_LEFT_XOFFSET 320
#define BMP_NUM_XOFFSET 240
#define BMP_ALPH1_XOFFSET 280
#define BMP_ALPH2_XOFFSET 360
#define BMP_STORE_XOFFSET 40

#define STD_INPUT_WAIT_TIME 20 'in 0.5 seconds

'------ ui_menu ------
#define VER_SPACING 9
#define HOR_SPACING 2
#define BMP_CANCEL_XOFFSET 0
#define BMP_ENTER_XOFFSET 40
#define BMP_UP_XOFFSET 80
#define BMP_DOWN_XOFFSET 120
#define MENU_MAX_LVL 5
#define MENU_MAX_ITEM_PER_LVL 16
#define MENU_DISPLAY_ITEM_PER_LVL 6
#define LCD_MENU_TOUT 0
#define DISPLAY_STR_LEN 150
#define MENU_STRING_LEN 255
#define SCROLL_BAR_WIDTH 15
#define SCROLL_ARROW_HEIGHT 12
#define SCROLL_MIN_HEIGHT 2
#define SCROLL_CURSOR_WIDTH 9
#define CURSOR_BAR_LEN_OFFSET 2
#define SCROLL_BORDER 2
#define LCD_BACKCOLOR 0
#define LCD_FORECOLOR 65535
#define LCD_DISABLE_BACKCOLOR 14823
#define LCD_DISABLE_FORECOLOR 33840
#define LCD_CURSORCOLOR 63080
#define LCD_DEAD_SCREEN_COLOR 63488
#define MENU_X 0
#define MENU_Y 7
#define MENU_W 320
#define MENU_H 187
#define MENU_MAX_LINE 6
#define UM_HOR_SPACING HOR_SPACING
#define UM_VER_SPACING VER_SPACING
#define MENU_SCRIPT_FILE "menu.xtxt"
#define USE_STG_TIMESTAMP 0

'------ ui_msg_box ------
#define MSG_BOX_X 0
#define MSG_BOX_Y 0
#define MSG_BOX_W lcd.width
#define MSG_BOX_H 185
#define MSB_HOR_SPACING 2
#define MSB_VER_SPACING 3

'------ ui_input_box -----
#define IB_MAX_LEN 64
#define LCD_FIX_FONT "11X19(H).bin"
#define INPUT_BOX_X MENU_X
#define INPUT_BOX_Y MENU_H+MENU_Y-9
#define INPUT_BOX_W MENU_W-MENU_X
#define INPUT_BOX_H 25
#define INPUT_BOX_CURSOR_OFFSET 2
#define INPUT_BOX_CURSOR_WIDTH 2
#define INPUT_BOX_INIT_POS 0
#define LCD_INPUTBOX_FORECOLOR 65535
#define LCD_INPUTBOX_BACKCOLOR 63080
#define UIB_HOR_SPACING HOR_SPACING
#define RESTART yes

'------ ui_list_box ------
#define SMALL_ICON_FILE "small_icons.bmp"
#define LIST_BOX_X 25
#define LIST_BOX_Y 15
#define LIST_BOX_W 270
#define LIST_BOX_H 160
#define SCROLL_ARROW_X_POS_OFFSET 0
#define SCROLL_ARROW_Y_POS_OFFSET 2
#define ULB_VER_SPACING VER_SPACING
#define ULB_HOR_SPACING HOR_SPACING`,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_lcd';
    }
}
